import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, Check, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "../i18n/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "./ui/dropdown-menu";

export const Navbar = () => {
  const { t, lang, setLang, languages } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/services", label: t.nav.services },
    { to: "/global-presence", label: t.nav.global },
    { to: "/contact", label: t.nav.contact },
  ];

  const currentLang = languages.find((l) => l.code === lang);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav py-3" : "py-5 bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                  active
                    ? "text-white bg-white/8"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="language-switcher-trigger"
                className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/10 text-zinc-300 hover:text-white hover:border-white/25 transition-colors text-sm"
              >
                <Globe className="h-4 w-4 text-[#00E5FF]" />
                <span className="hidden sm:inline font-mono text-xs uppercase">{currentLang?.code}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#0F0F12] border-white/10 text-white min-w-[190px]"
              data-testid="language-menu"
            >
              {languages.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className="cursor-pointer focus:bg-white/8 focus:text-white flex items-center justify-between"
                >
                  <span className="flex flex-col">
                    <span className="text-sm">{l.native}</span>
                    <span className="text-[11px] text-zinc-500">{l.label}</span>
                  </span>
                  {lang === l.code && <Check className="h-4 w-4 text-[#1E90FF]" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            onClick={() => navigate("/contact")}
            data-testid="nav-cta-button"
            className="hidden md:inline-flex items-center px-5 py-2.5 rounded-full bg-[#1E90FF] hover:bg-[#3B82F6] text-white text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 shadow-[0_0_24px_rgba(30,144,255,0.35)]"
          >
            {t.common.getInTouch}
          </button>

          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mt-3 mx-4 rounded-2xl glass p-4 animate-fade-up" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
                className={`px-4 py-3 rounded-xl text-sm font-medium ${
                  location.pathname === l.to ? "bg-white/10 text-white" : "text-zinc-300"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="mt-2 px-4 py-3 rounded-xl bg-[#1E90FF] text-white text-sm font-semibold text-center"
            >
              {t.common.getInTouch}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
